import styled, { css } from "styled-components";
import {
  BACKGROUND_COLORS,
  COLORS,
  LAYER_COLORS,
} from "../../constants/design/colors";
import { BORDER_RADIUS, COMPONENTS, SPACING } from "../../constants/design/spacing";
import {
  ButtonThemeEnum,
  buttonBgColor,
  buttonTextColor,
} from "../Buttons/styles";
import { RowActionButtonWrapper } from "../shared/RowActionButton/style";
import { TableWrapper } from "../shared/Table/style";

export const RowActionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;

  > ${RowActionButtonWrapper} {
    &:not(:last-of-type) {
      margin-right: ${SPACING.two}px;
    }
  }
`;

export const ClosePositionButton = styled(RowActionButtonWrapper)`
  border-radius: ${BORDER_RADIUS.three}px;
  margin-right: ${SPACING.three}px;
  background-color: ${BACKGROUND_COLORS.five};

  &:hover {
    opacity: 0.8;
  }
`;

export const PositionsTableWrapper = styled(TableWrapper)`
  height: 100%;
  overflow: auto !important;

  > table {
    width: max-content;
    min-width: 100%;
  }

  thead {
    position: sticky;
  }

  th,
  td {
    box-sizing: border-box;

    /* ── Explicit width per column (desktop) ── */
    &[data-column="instrument_name"] {
      width: 132px;
      min-width: 132px;
    }
    &[data-column="amount"] {
      width: 104px;
      min-width: 104px;
    }
    &[data-column="positionValue"] {
      width: 116px;
      min-width: 116px;
    }
    &[data-column="avg_entry_price"] {
      width: 108px;
      min-width: 108px;
    }
    &[data-column="mark_price"] {
      width: 104px;
      min-width: 104px;
    }
    &[data-column="unrealized_pnl"] {
      width: 124px;
      min-width: 124px;
    }
    &[data-column="realized_pnl"] {
      width: 116px;
      min-width: 116px;
    }
    /* Wider than the other numeric columns: holds the TP + SL prices AND the
       edit/add affordance side by side. */
    &[data-column="tpsl"] {
      width: 132px;
      min-width: 132px;
    }

    /* ── Sticky right-hand columns (offset = sum of widths to their right) ── */
    &[data-column="liquidation_price"] {
      width: 120px;
      min-width: 120px;
      position: sticky;
      right: 264px;
      z-index: 2;
      background: ${LAYER_COLORS.one};
    }

    &[data-column="maintenance_margin"] {
      width: 132px;
      min-width: 132px;
      padding-left: ${SPACING.three}px;
      padding-right: ${SPACING.three}px;
      position: sticky;
      right: 132px;
      z-index: 2;
      background: ${LAYER_COLORS.one};
    }

    &[data-column="closePositionRektMeter"] {
      width: 132px;
      min-width: 132px;
      padding-left: ${SPACING.two}px;
      padding-right: ${SPACING.two}px;
      position: sticky;
      right: 0;
      z-index: 2;
      background: ${LAYER_COLORS.one};
    }
  }

  tbody td {
    background: ${LAYER_COLORS.one};
  }

  thead th[data-column="liquidation_price"],
  thead th[data-column="maintenance_margin"],
  thead th[data-column="closePositionRektMeter"] {
    z-index: 4;
  }
`;

// `$pnl` (when provided) drives a DIRECTIONAL background/text from the
// user-configurable up/down scheme — bypassing the shared ButtonThemeEnum map
// (which is also used by fixed-color status buttons). Falls back to the legacy
// `theme` prop, then the neutral highlight, when `$pnl` is not passed.
const summaryBg = (pnl?: number, theme?: ButtonThemeEnum) =>
  pnl !== undefined && pnl !== 0
    ? pnl > 0
      ? COLORS.up.five
      : COLORS.down.five
    : theme !== undefined
    ? buttonBgColor[theme]
    : COLORS.highlight.five;

export const SummaryTableRow = styled.tr<{
  theme?: ButtonThemeEnum;
  $pnl?: number;
}>`
  background-color: ${({ theme, $pnl }) => summaryBg($pnl, theme)};
  color: ${({ theme, $pnl }) =>
    $pnl !== undefined && $pnl !== 0
      ? $pnl > 0
        ? COLORS.up.one
        : COLORS.down.one
      : theme !== undefined
      ? buttonTextColor[theme as ButtonThemeEnum]
      : COLORS.highlight.one};

  height: ${COMPONENTS.tableHeader}px !important;
  td {
    background-color: transparent !important;
  }
  &:hover {
    td {
      background-color: ${({ theme, $pnl }) => summaryBg($pnl, theme)};
    }
  }
  cursor: default !important;
`;

export const PositionTableRow = styled.tr<{ $isLong?: boolean }>`
  cursor: pointer;

  /* Hyperliquid-style side indicator: a left→right colour gradient that fades
     out across the first cell, plus a 2px left bar. The gradient is layered as
     a background-image so the cell keeps its opaque background-color. */
  ${({ $isLong }) =>
    $isLong !== undefined &&
    css`
      & > td:first-child {
        background-image: linear-gradient(
          to right,
          ${$isLong ? COLORS.up.three : COLORS.down.three},
          transparent 120px
        );
        box-shadow: inset 2px 0 0
          ${$isLong ? COLORS.up.one : COLORS.down.one};
      }
    `}
`;

export const HeaderColumn = styled.td`
  position: relative !important;
`;
